import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/mtm/api')

/** 场站状态：启用可被计划区段当起讫点，停用只是档案停用，历史引用保留 */
export type HubStatus = 'active' | 'disabled'

/** 场站类型：与后端 internal/hub/model.go 的 Kinds 同源两份 */
export type HubKind = 'depot' | 'rail_yard' | 'port' | 'airport'

/** 场站档案。字段与 services/mtm/internal/hub/model.go 的 Hub 一一对应。 */
export interface Hub {
  id: number
  tenant_id: number
  code: string
  name: string
  kind: HubKind
  city: string
  address: string
  status: HubStatus
  created_at: string
  updated_at: string
}

export interface HubInput {
  code: string
  name?: string
  kind: HubKind
  city?: string
  address?: string
  status: HubStatus
}

export interface HubQuery extends PageQuery {
  keyword?: string
  status?: string
  kind?: string
}

/** 表单态：空文本字段用 '' 表示没填 */
export interface HubForm {
  code: string
  name: string
  kind: HubKind
  city: string
  address: string
  status: HubStatus
}

/** 场站类型字典，顺序与后端 Kinds 一致，即前端下拉的展示顺序 */
export const KINDS = ['depot', 'rail_yard', 'port', 'airport'] as const

/** 与后端 services/mtm/internal/hub/model.go 的 KindNames 同源两份 */
export const KIND_LABELS: Record<HubKind, string> = {
  depot: '仓库/堆场',
  rail_yard: '铁路站场',
  port: '港口码头',
  airport: '机场货站',
}

/** 与后端 services/mtm/internal/hub/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<HubStatus, string> = {
  active: '启用',
  disabled: '停用',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<HubStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  active: 'success',
  disabled: 'info',
}

/** 表单 → 请求体：空字段不进 body（undefined 在 JSON.stringify 里会被丢掉） */
export function hubInput(form: HubForm): HubInput {
  const input: HubInput = {
    code: form.code.trim(),
    kind: form.kind,
    status: form.status,
  }
  const name = form.name.trim()
  if (name) input.name = name
  const city = form.city.trim()
  if (city) input.city = city
  const address = form.address.trim()
  if (address) input.address = address
  return input
}

export const listHubs = (q: HubQuery): Promise<PageResult<Hub>> =>
  api.get<PageResult<Hub>>('/hubs', { ...q })

export const createHub = (input: HubInput): Promise<Hub> => api.post<Hub>('/hubs', input)

export const updateHub = (id: number, input: HubInput): Promise<Hub> =>
  api.put<Hub>(`/hubs/${id}`, input)

export const deleteHub = (id: number): Promise<void> => api.del<void>(`/hubs/${id}`)

/** 下拉参照用的全量场站：后端 size 上限 200 */
export const listHubOptions = (): Promise<Hub[]> =>
  listHubs({ page: 1, size: 200 }).then((page) => page.list)
